import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { transferPasswordValidators } from '@app/shared/helpers/forms';
import { TransferCommonParameters } from '@app/client/uploader/interfaces/transfer';
import { PrepareActions } from '@app/client/uploader/store/actions';
import * as fromRoot from '@app/reducers';
import { Customization } from '@app/shared/models/customization';
import { TransferSettings } from '@app/shared/models/domainSettings';
import { Promotion } from '@app/shared/models/promotion';
import { DialogService } from '@ngneat/dialog';
import { select, Store } from '@ngrx/store';
import { GetRecordOutput } from '@smash-sdk/domain/ejs/01-2024';
import { Observable, Subscription } from 'rxjs';
import { Constant } from 'src/constant';
import * as fromUploader from '@app/client/uploader/store/reducers';
import { ModalCustomizationReceiverComponent } from '../../modals/modal-customization-receiver/modal-customization-receiver.component';
import { ModalPromotionComponent } from '../../modals/modal-promotion/modal-promotion.component';
@Component({
  selector: 'app-config-send-parameters',
  templateUrl: './config-send-parameters.component.html',
  styleUrls: ['./config-send-parameters.component.scss']
})
export class ConfigSendParametersComponent implements OnInit, OnDestroy {

  @Input() uploadFormMode: string;
  @Input() drawerExpanded: boolean;
  @Input() promotions: Promotion[];
  @Input() promotionSelected: Promotion;
  @Input() promotionLoading: boolean;
  @Input() customizationSelected: Customization;
  @Input() customizationLoading: boolean;
  @Input() transferSettings: TransferSettings;
  @Input() transferCommonParameters: TransferCommonParameters;

  isPremium$: Observable<boolean>;
  transferPassword$: Observable<string>;
  domain$: Observable<GetRecordOutput['record']>;

  parametersForm: FormGroup;

  subscriptions: Subscription[] = [];

  translations = {
    Full: 'UPLOADER_CONFIG_SMASH_PREVIEW_NOTIFICATION_ENABLED',
    All: 'UPLOADER_CONFIG_SMASH_PREVIEW_NOTIFICATION_ENABLED',
    None: 'UPLOADER_CONFIG_SMASH_PREVIEW_NOTIFICATION_DISABLED'
  };

  constructor(private readonly fb: FormBuilder, private store$: Store<fromRoot.State & fromUploader.State>, private dialog: DialogService) {
    this.isPremium$ = this.store$.pipe(select(fromRoot.getIsPremium));
    this.domain$ = this.store$.pipe(select(fromRoot.getCurrentDomain));
    this.transferPassword$ = this.store$.pipe(select(fromUploader.getTransferPassword));
  }

  ngOnInit() {
    this.buildParametersForm();
    this.initUploaderParams();
    this.listenToFormChanges();
    this.subscribeToPasswordChanges();
  }

  subscribeToPasswordChanges() {
    this.subscriptions.push(
      this.transferPassword$.subscribe(password => {
        if (!!password) {
          // Guard the FORM, not just the control. The `?.` here used to sit on `password` alone, which is
          // the one of the two that is never missing: a built form always has the control, and an unbuilt
          // form has no `.controls` to reach it through.
          //
          // Defensive rather than corrective in THIS file: the order here has always been build-then-
          // subscribe, so no emission ever arrived before the form existed. The mobile LINK variant did have
          // the inverted order and did throw; the guard is what makes both robust to a future reordering.
          // See cva-contract.spec.ts and the link form's spec.
          this.parametersForm?.controls.password?.setValue(password, { emitEvent: false });
          // Re-publish, because `emitEvent: false` recomputes the control's validity but deliberately does
          // NOT fire statusChanges -- and the status publication rides on statusChanges. Without this, a
          // password restored here stays unpublished: the store keeps whatever was published at mount, when
          // the control was still empty, and the send gate then refuses a transfer whose password is present
          // and valid, against a drawer showing a correct field with nothing to explain the refusal.
          //
          // That state is reachable exactly as initUploaderParams below describes: transferCommonParameters
          // is an @Input snapshot bound through an async pipe, so it can lack a password the store already
          // holds. Both statuses are re-published rather than only the password one -- leaving one of the two
          // stale while fixing the other is how the next reader inherits a puzzle.
          this.publishFormStatuses();
        }
      })
    );
  }

  buildParametersForm(): void {
    const { password, availabilityDuration, preview, notification } = this.transferCommonParameters;
    // TO FIX: transfer.availabilityDuration unit in days VS dropbox.transfer.availibilityDuraion in seconds
    const isSeconds = this.transferSettings.availabilityDuration.default.toString().length > 3
    const availabilityDurationInDays = {
      default: isSeconds ? this.transferSettings.availabilityDuration.default / Constant.oneDayInSecond : this.transferSettings.availabilityDuration.default,
      min: isSeconds ? this.transferSettings.availabilityDuration.min / Constant.oneDayInSecond : this.transferSettings.availabilityDuration.min,
      max: isSeconds ? this.transferSettings.availabilityDuration.max / Constant.oneDayInSecond : this.transferSettings.availabilityDuration.max,
    }
    // END TO FIX
    this.parametersForm = this.fb.group({
      // The account policy, applied from one place: see transferPasswordValidators for why `required`
      // must be honoured here at all, and why `active` has to gate it.
      password: [password ? password : '', transferPasswordValidators(this.transferSettings.password)],
      availabilityDuration: [
        availabilityDuration > 0 ? availabilityDuration / Constant.oneDayInSecond : availabilityDurationInDays.default,
        [
          Validators.min(availabilityDurationInDays.min),
          Validators.max(availabilityDurationInDays.max),
          Validators.required,
        ]
      ],
      preview: [
        preview ? preview === 'Full' : this.transferSettings.preview.default === Constant.previewStatus.Full ? true : false,
      ],
      senderNotification: [this.transferSettings.notification?.sender?.enabled],
      receiverNotification: [this.transferSettings.notification?.receiver?.enabled],
      linkNotification: [this.transferSettings.notification?.link?.enabled],
      downloadNotification: [this.transferSettings.notification?.download?.enabled],
      noDownloadNotification: [this.transferSettings.notification?.noDownload?.enabled],
    });
  }

  get f() { return this.parametersForm.controls; }

  initUploaderParams(): void {
    const { password
      , availabilityDuration
      , preview
      , senderNotification
      , receiverNotification
      , linkNotification
      , downloadNotification
      , noDownloadNotification } = this.parametersForm.value;
    // Seed the store from the form's initial values -- but NEVER an empty password.
    //
    // This form is DERIVED from the transferCommonParameters @Input, which the parent binds through
    // `transferCommonParameters$ | async`. So a mount that happens before the store has been projected,
    // or with any snapshot that lacks the password, builds an empty control -- and publishing that
    // emptiness overwrites a password already in the store. The reported symptom is exactly that: the
    // collapsed drawer's summary then reads UPLOADER_NO_PASSWORD, and the input and its eye icon are not
    // on screen to contradict it, so the password looks silently forgotten.
    //
    // The asymmetry is deliberate. Availability, preview and the notification flags have real defaults
    // coming from transferSettings, so seeding them supplies information. The password has no default:
    // it is either something the user typed or nothing at all, so seeding an empty one can only ever
    // destroy information. Clearing a password on purpose still works -- that travels through
    // listenToFormChanges below, which is user-driven and unaffected.
    if (password) {
      this.store$.dispatch(PrepareActions.SetTransferPassword({ password }));
    }
    this.store$.dispatch(PrepareActions.SetTransferAvailability({ availability: availabilityDuration }));
    this.store$.dispatch(PrepareActions.SetTransferPreviewType({ preview: preview ? 'Full' : 'None' }));
    this.store$.dispatch(PrepareActions.SetTransferNotification({
      notification: {
        sender: { enabled: senderNotification },
        receiver: { enabled: receiverNotification },
        link: { enabled: linkNotification },
        download: { enabled: downloadNotification },
        noDownload: { enabled: noDownloadNotification },
      }
    }));
  }

  listenToFormChanges(): void {
    this.subscriptions.push(
      this.parametersForm.valueChanges.subscribe(parametersFormValues => {
        const { password, availabilityDuration, preview, senderNotification, receiverNotification, linkNotification, downloadNotification, noDownloadNotification } = parametersFormValues;
        this.store$.dispatch(PrepareActions.SetTransferPassword({ password }));
        this.store$.dispatch(PrepareActions.SetTransferAvailability({ availability: availabilityDuration }));
        this.store$.dispatch(PrepareActions.SetTransferPreviewType({ preview: preview ? 'Full' : 'None' }));
        this.store$.dispatch(PrepareActions.SetTransferNotification({
          notification: {
            sender: { enabled: senderNotification },
            receiver: { enabled: receiverNotification },
            link: { enabled: linkNotification },
            download: { enabled: downloadNotification },
            noDownload: { enabled: noDownloadNotification },
          }
        }));
      })
    );

    // Publish the CURRENT status before subscribing. Angular does not emit statusChanges on
    // construction, so the form's initial validity was never dispatched and the store kept its initial
    // null until the user happened to change a field. That cost nothing while none of this drawer's
    // validators could realistically fail -- and became the whole problem once the send started reading
    // configFormStatus to refuse a transfer whose forced password is missing: on the FIRST send, before
    // the user has touched anything, the store said null rather than INVALID, so the refusal never fired
    // and the transfer went out empty. Caught by the forced-password e2e, which is the only level that
    // exercises the store round trip end to end.
    //
    // This publication is also what keeps the send gate armed across a reset, and that depends on an
    // ordering worth naming: `resetDrawer` sets configFormStatus back to null, and it is dispatched from
    // PrepareComponent's CONSTRUCTOR -- which always runs before this component's ngOnInit, since the drawer
    // is created during the parent's first change detection. So the null is overwritten by the real status
    // and never survives. A future `resetDrawer` dispatched from anywhere else, once the drawer already
    // exists, would clear the status while the form is still invalid and silently disarm the gate until the
    // user happened to edit a field -- which is precisely the hole this line closes.
    this.publishFormStatuses();
    this.subscriptions.push(
      this.parametersForm.statusChanges.subscribe(() => this.publishFormStatuses())
    );
  }

  // The drawer's validity, published as two separate facts because they gate two different things: the
  // whole form decides whether the drawer may be CLOSED, the password control alone decides whether the
  // send is refused. See isSettingsDrawerBlockingSubmit for why the send must not read the whole form.
  //
  // Factored out because there are three callers and every one of them matters: mount (Angular does not
  // emit statusChanges on construction), every statusChanges, and the silent password restore in
  // subscribeToPasswordChanges, whose `emitEvent: false` recomputes validity without emitting. Miss any
  // one of the three and the store holds a status the form has already left behind.
  publishFormStatuses(): void {
    if (!this.parametersForm) {
      return;
    }
    this.store$.dispatch(PrepareActions.setConfigFormStatus({ configFormStatus: this.parametersForm.status }));
    this.store$.dispatch(PrepareActions.setConfigPasswordStatus({ configPasswordStatus: this.parametersForm.controls.password.status }));
  }

  openModalCustomization() {
    if (!!this.customizationSelected) {
      this.dialog.open(ModalCustomizationReceiverComponent, {
        closeButton: false,
        enableClose: false,
        height: '80vh',
        width: '80vw'
      });
    }
  }

  openModalPromotion() {
    if (!!this.promotionSelected) {
      this.dialog.open(ModalPromotionComponent, {
        closeButton: false,
        enableClose: false,
        height: '80vh',
        width: '80vw'
      });
    }
  }

  get isUpdateCustomizationActive(): boolean {
    return this.transferSettings?.customization?.active;
  }

  get isUpdatePromotionActive(): boolean {
    return this.transferSettings?.promotion?.active;
  }

  get isUpdateAvailabilityDurationActive(): boolean {
    return this.transferSettings?.availabilityDuration?.active;
  }

  get isUpdatePasswordActive(): boolean {
    return this.transferSettings?.password?.active;
  }

  get isUpdatePreviewActive(): boolean {
    return this.transferSettings?.preview?.active;
  }

  get isDisplaySenderNotificationActive(): boolean {
    return this.transferSettings?.notification?.sender?.active;
  }

  get isDisplayLinkNotificationActive(): boolean {
    return this.transferSettings?.notification?.link?.active;
  }

  get isDisplayDownloadNotificationActive(): boolean {
    return this.transferSettings?.notification?.download?.active;
  }

  get isDisplayNoDownloadNotificationActive(): boolean {
    return this.transferSettings?.notification?.noDownload?.active;
  }

  get previewOptions(): any {
    return this.transferSettings?.preview?.options;
  }

  ngOnDestroy() {
    this.subscriptions.map(subscription => subscription.unsubscribe());
    this.subscriptions.length = 0;
  }

}
